import { endOfDay, endOfMonth, format, startOfDay, startOfMonth, subDays } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

import {
  PRACTICE_LEDGER_DATE_RANGE_PRESET,
  PRACTICE_LEDGER_DATE_RANGE_PRESET_LABEL,
  PRACTICE_LEDGER_LAST_30_DAY_COUNT,
  PRACTICE_LEDGER_LAST_90_DAY_COUNT,
  type PracticeLedgerDateRange,
  type PracticeLedgerDateRangePreset,
  type PracticeLedgerNamedDateRangePreset,
} from "@/billing/types/practice-ledger";
import { localDateStr } from "@/utils/datetime";

const FHIR_CALENDAR_DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Inclusive start/end for a named Show: preset, relative to `now`.
 *
 * Current Month is the calendar month containing `now`. Last 30 / Last 90 count today as day 1.
 */
export function getPracticeLedgerDateRange(
  preset: PracticeLedgerNamedDateRangePreset,
  now = new Date(),
): PracticeLedgerDateRange {
  if (preset === PRACTICE_LEDGER_DATE_RANGE_PRESET.CurrentMonth) {
    return {
      startDate: startOfMonth(now),
      endDate: endOfMonth(now),
    };
  }

  const dayCount =
    preset === PRACTICE_LEDGER_DATE_RANGE_PRESET.Last30Days
      ? PRACTICE_LEDGER_LAST_30_DAY_COUNT
      : PRACTICE_LEDGER_LAST_90_DAY_COUNT;

  return {
    startDate: startOfDay(subDays(now, dayCount - 1)),
    endDate: endOfDay(now),
  };
}

/**
 * Inclusive custom range. Each bound is normalized to the start/end of its calendar day.
 * When `endDate` falls before `startDate`, the range is that single start day.
 */
export function getPracticeLedgerCustomDateRange(
  startDate: Date,
  endDate: Date,
): PracticeLedgerDateRange {
  const normalizedStartDate = startOfDay(startDate);
  const normalizedEndDate = endOfDay(endDate);
  if (normalizedEndDate < normalizedStartDate) {
    return {
      startDate: normalizedStartDate,
      endDate: endOfDay(normalizedStartDate),
    };
  }
  return { startDate: normalizedStartDate, endDate: normalizedEndDate };
}

/** Trigger copy: preset name for named windows, `MM/dd/yyyy – MM/dd/yyyy` for Custom. */
export function formatPracticeLedgerDateRangeTriggerLabel(
  preset: PracticeLedgerDateRangePreset,
  dateRange: PracticeLedgerDateRange,
): string {
  if (preset === PRACTICE_LEDGER_DATE_RANGE_PRESET.Custom) {
    return `${format(dateRange.startDate, "MM/dd/yyyy")} – ${format(dateRange.endDate, "MM/dd/yyyy")}`;
  }
  return PRACTICE_LEDGER_DATE_RANGE_PRESET_LABEL[preset];
}

/**
 * Full calendar `YYYY-MM-DD` from a FHIR date or dateTime. Year-only and year-month precision
 * cannot sit on a Transaction Date, so they are omitted and fall outside the Show: window.
 */
export function toCalendarDate(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const calendarDate = localDateStr(value);
  if (!FHIR_CALENDAR_DAY.test(calendarDate)) return undefined;
  return calendarDate;
}

/**
 * UTC `YYYY-MM-DD` of a Show: bound. Invoice `date`, PaymentReconciliation `paymentDate`, and
 * `toCalendarDate` all compare as UTC calendar days, so window filters use this rather than the
 * local-time `yyyy-MM-dd` of the Date object.
 */
export function formatUtcCalendarDate(value: Date): string {
  return formatInTimeZone(value, "UTC", "yyyy-MM-dd");
}

/**
 * Inclusive calendar-day membership in a Show: window. `calendarDate` is `YYYY-MM-DD`; missing
 * values are outside the range. Bounds are UTC calendar days so they match `toCalendarDate`.
 */
export function isCalendarDateInRange(
  calendarDate: string | undefined,
  dateRange: PracticeLedgerDateRange,
): boolean {
  if (!calendarDate) return false;
  const start = formatUtcCalendarDate(dateRange.startDate);
  const end = formatUtcCalendarDate(dateRange.endDate);
  return calendarDate >= start && calendarDate <= end;
}
